import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Card, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Avatar, ToggleButton, ToggleButtonGroup,
  TablePagination,
} from '@mui/material';
import { Add, Refresh, Delete, Visibility, YouTube, CheckCircle, Cancel, VideoLibrary, PlayCircle } from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';

const CONTENT_TYPES = [
  { key: 'Blogs', label: 'Blogs', uploadField: 'blog_image', imageField: 'blog_image', hasText: true },
  { key: 'Programs', label: 'Programs', uploadField: 'program_image', imageField: 'program_image', hasText: true, hasAgeGroup: true },
  { key: 'Gallery', label: 'Gallery Images', uploadField: 'gallery', imageField: 'image', hasText: false },
  { key: 'Videos', label: 'Video Gallery', uploadField: 'video-img', imageField: 'thumbnail', hasText: false, hasYouTube: true },
  { key: 'Sliders', label: 'Sliders', uploadField: 'slider-img', imageField: 'slider_img', hasText: false },
  { key: 'Testimonials', label: 'Testimonials', uploadField: 'testimonial-img', imageField: 'testimonial_img', hasText: true, hasRating: true },
];

const IMAGE_ONLY = ['Gallery', 'Sliders', 'Videos'];

export default function ContentManager() {
  const [contentType, setContentType] = useState('Blogs');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [toggling, setToggling] = useState({});
  const [testimonialStatus, setTestimonialStatus] = useState('Inactive');
  const [form, setForm] = useState({ tittle: '', description: '', details: '', age_group: '', name: '', rating: '5', YtLink: '' });
  const [image, setImage] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [uploading, setUploading] = useState(false);
  const [videoMode, setVideoMode] = useState('youtube'); // 'youtube' | 'local'
  const [videoFile, setVideoFile] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoPath, setVideoPath] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const config = CONTENT_TYPES.find(t => t.key === contentType);

  const getApi = (type) => ({
    Blogs: { get: apiList.getBlogs, add: apiList.addBlogs, del: apiList.deleteBlog, upload: apiList.addBlogsImage },
    Programs: { get: apiList.getPrograms, add: apiList.addPrograms, del: apiList.deleteProgram, upload: apiList.addProgramImage },
    Gallery: { get: apiList.getGalleryImages, add: apiList.addGallery, del: apiList.deleteGallery, upload: apiList.addGalleryImage },
    Videos: { get: apiList.getVideo, add: apiList.addVideoGallery, del: apiList.deleteVideo, upload: apiList.addVideoImage },
    Sliders: { get: apiList.getSliders, add: apiList.addSlider, del: apiList.deleteSlider, upload: apiList.addSliderImage },
    Testimonials: { get: apiList.getTestimonials, add: apiList.addTestimonial, del: apiList.deleteTestimonial, upload: apiList.addTestimonialImage },
  }[type]);

  const getResponseKey = (type) => ({
    Blogs: 'blogs', Programs: 'programs', Gallery: 'images',
    Videos: 'videos', Sliders: 'sliders', Testimonials: 'testimonials',
  }[type]);

  const getImageSrc = (item) => {
    if (!item) return null;
    if (contentType === 'Blogs') return item.blog_image;
    if (contentType === 'Programs') return item.program_image;
    if (contentType === 'Gallery') return item.image;
    if (contentType === 'Videos') return item.thumbnail;
    if (contentType === 'Sliders') return item.slider_img;
    if (contentType === 'Testimonials') return item.testimonial_img;
    return null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    const api = getApi(contentType);
    const params = contentType === 'Testimonials' ? { status: testimonialStatus } : {};
    const data = await InvokeApi(Config.main_url + api.get, params);
    setRecords(data?.[getResponseKey(contentType)] || []);
    setLoading(false);
  }, [contentType, testimonialStatus]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (item) => {
    const id = item.ID;
    const isActive = item.status === 'Active' || !item.status;
    setToggling(p => ({ ...p, [id]: true }));
    if (contentType === 'Testimonials') {
      await InvokeApi(Config.main_url + apiList.approveTestimonails, {
        id,
        status: isActive ? 'Inactive' : 'Active',
      });
    } else {
      const api = getApi(contentType);
      await InvokeApi(Config.main_url + api.del, {
        id,
        status: isActive ? 'Inactive' : 'Active',
      });
    }
    setToggling(p => ({ ...p, [id]: false }));
    load();
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    setImagePath(''); // clear previous path while uploading
    const api = getApi(contentType);
    const fd = new FormData();
    fd.append(config.uploadField, file);
    try {
      const res = await postImg(Config.main_url + api.upload, fd);
      if (res?.code === '200') {
        setImagePath(res.imageName || '');
      } else {
        alert('Image upload failed: ' + (res?.message || 'Unknown error'));
        setImage(null);
      }
    } catch (err) {
      alert('Image upload error: ' + err.message);
      setImage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const api = getApi(contentType);
    if (config.hasText && !form.tittle && !form.name) { alert('Title/Name is required'); return; }
    if (config.hasYouTube && videoMode === 'youtube' && !form.YtLink) { alert('YouTube URL is required'); return; }
    if (config.hasYouTube && videoMode === 'local' && !videoPath) { alert('Please wait for video to finish uploading'); return; }
    if (!config.hasYouTube && !imagePath) { alert('Image is required — please wait for upload to finish'); return; }
    if (uploading || videoUploading) { alert('Please wait for the upload to finish'); return; }

    const adminId = localStorage.getItem('admin_id') || '1';

    setSaving(true);
    let payload;
    if (contentType === 'Blogs' || contentType === 'Programs') {
      payload = { tittle: form.tittle, description: form.description, details: form.details, [config.imageField]: imagePath, createdBy: adminId };
      if (contentType === 'Programs') payload.age_group = form.age_group;
    } else if (contentType === 'Gallery') {
      payload = { image: imagePath, createdBy: adminId };
    } else if (contentType === 'Videos') {
      if (videoMode === 'youtube') {
        payload = { YtLink: form.YtLink, thumbnail: imagePath, video_type: 'youtube', createdBy: adminId };
      } else {
        payload = { video_url: videoPath, thumbnail: imagePath, video_type: 'local', createdBy: adminId };
      }
    } else if (contentType === 'Sliders') {
      payload = { slider_img: imagePath, createdBy: adminId };
    } else if (contentType === 'Testimonials') {
      payload = { testimonial_img: imagePath, name: form.name, rating: parseInt(form.rating) || 5, details: form.details, createdBy: adminId };
    }

    const data = await InvokeApi(Config.main_url + api.add, payload);
    setSaving(false);
    if (data?.code === '200') { setDialog(false); resetForm(); load(); }
    else alert(data?.message || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    const api = getApi(contentType);
    await InvokeApi(Config.main_url + api.del, { id });
    load();
  };

  const resetForm = () => {
    setForm({ tittle: '', description: '', details: '', age_group: '', name: '', rating: '5', YtLink: '' });
    setImage(null);
    setImagePath('');
    setVideoFile(null);
    setVideoPath('');
    setVideoMode('youtube');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const isImageOnly = IMAGE_ONLY.includes(contentType);
  const paginated = records.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Content Manager</Typography>
          <Typography variant="body2" color="text.secondary">{records.length} {contentType.toLowerCase()}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => { resetForm(); setDialog(true); }}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add {contentType === 'Gallery' ? 'Image' : contentType === 'Sliders' ? 'Slider' : contentType.slice(0, -1)}
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {CONTENT_TYPES.map(t => (
            <Chip key={t.key} label={t.label} clickable onClick={() => setContentType(t.key)}
              sx={{
                fontWeight: 700, fontSize: 12,
                background: contentType === t.key ? '#1a237e' : '#e8eaf6',
                color: contentType === t.key ? 'white' : '#1a237e',
                '&:hover': { background: contentType === t.key ? '#1a237e' : '#c5cae9' },
              }}
            />
          ))}
          {contentType === 'Testimonials' && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography fontSize={12} color="text.secondary" fontWeight={600}>Show:</Typography>
              {['Inactive', 'Active'].map(s => (
                <Chip key={s} label={s === 'Inactive' ? 'Pending Approval' : 'Approved'} clickable
                  onClick={() => setTestimonialStatus(s)}
                  sx={{
                    fontWeight: 700, fontSize: 11,
                    background: testimonialStatus === s ? (s === 'Active' ? '#2e7d32' : '#e65100') : '#f5f6fa',
                    color: testimonialStatus === s ? 'white' : 'text.secondary',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>Image</TableCell>
                    {!isImageOnly && <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>Title</TableCell>}
                    {!isImageOnly && (
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>
                        {contentType === 'Testimonials' ? 'Rating' : 'Description'}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isImageOnly ? 3 : 5} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No {contentType.toLowerCase()} found
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((r, i) => {
                    const imgSrc = getImageSrc(r);
                    return (
                      <TableRow key={r.ID || i} hover>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                        <TableCell>
                          {imgSrc ? (
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                              <Box component="img" src={imgSrc} alt="content"
                                sx={{ width: 70, height: 50, objectFit: 'cover', borderRadius: 1 }} />
                              {contentType === 'Videos' && (
                                <Box sx={{
                                  position: 'absolute', top: 3, right: 3,
                                  background: r.video_type === 'local' ? '#1a237e' : '#FF0000',
                                  borderRadius: 0.5, px: 0.5, py: 0.2,
                                }}>
                                  <Typography fontSize={8} color="white" fontWeight={700}>
                                    {r.video_type === 'local' ? 'LOCAL' : 'YT'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ) : contentType === 'Videos' ? (
                            <Avatar variant="rounded" sx={{ width: 70, height: 50, background: r.video_type === 'local' ? '#e8eaf6' : '#ffebee', borderRadius: 1 }}>
                              {r.video_type === 'local' ? <VideoLibrary sx={{ color: '#1a237e' }} /> : <YouTube sx={{ color: '#FF0000' }} />}
                            </Avatar>
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 70, height: 50, background: '#e8eaf6', borderRadius: 1 }}>
                              🖼️
                            </Avatar>
                          )}
                        </TableCell>
                        {!isImageOnly && (
                          <TableCell>
                            <Typography fontWeight={600} fontSize={13}>{r.tittle || r.name || '—'}</Typography>
                            {r.age_group && <Typography fontSize={11} color="text.secondary">Age: {r.age_group}</Typography>}
                          </TableCell>
                        )}
                        {!isImageOnly && (
                          <TableCell>
                            {contentType === 'Testimonials' ? (
                              <Box>
                                <Chip label={`⭐ ${r.rating || 5}`} size="small" sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700, mb: 0.5 }} />
                                {r.details && (
                                  <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{r.details}</Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography fontSize={12} color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                {r.description || r.details || '—'}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => setViewItem(r)} sx={{ color: '#1a237e' }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {contentType === 'Testimonials' && (
                            toggling[r.ID] ? (
                              <CircularProgress size={16} sx={{ color: '#1a237e', mx: 0.5 }} />
                            ) : testimonialStatus === 'Inactive' ? (
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => handleToggle(r)}
                                  sx={{ color: '#2e7d32', background: '#e8f5e9', borderRadius: 1.5, mx: 0.3 }}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Set Inactive">
                                <IconButton size="small" onClick={() => handleToggle(r)}
                                  sx={{ color: '#e65100', background: '#fff3e0', borderRadius: 1.5, mx: 0.3 }}>
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(r.ID)} sx={{ color: '#c62828' }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={records.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
          </>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add {contentType === 'Gallery' ? 'Image' : contentType === 'Sliders' ? 'Slider' : contentType.slice(0, -1)}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {!config.hasYouTube && (
              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }} disabled={uploading}>
                  {uploading ? 'Uploading...' : image ? `✓ ${image.name}` : 'Upload Image *'}
                  <input type="file" hidden accept="image/*" onChange={e => {
                    const file = e.target.files[0];
                    if (file) { setImage(file); handleImageUpload(file); }
                  }} />
                </Button>
                {image && !uploading && (
                  <Box component="img" src={URL.createObjectURL(image)} alt="preview"
                    sx={{ mt: 1, width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 2 }} />
                )}
              </Grid>
            )}

            {config.hasYouTube && (
              <>
                {/* Video mode toggle */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[
                      { key: 'youtube', label: 'YouTube URL', icon: <YouTube sx={{ fontSize: 16, mr: 0.5 }} /> },
                      { key: 'local', label: 'Upload Video', icon: <VideoLibrary sx={{ fontSize: 16, mr: 0.5 }} /> },
                    ].map(m => (
                      <Box key={m.key} onClick={() => setVideoMode(m.key)}
                        sx={{
                          display: 'flex', alignItems: 'center', px: 2, py: 1,
                          borderRadius: 2, cursor: 'pointer', border: '1.5px solid',
                          borderColor: videoMode === m.key ? '#1a237e' : '#e0e0e0',
                          background: videoMode === m.key ? '#e8eaf6' : '#fafafa',
                          fontWeight: 700, fontSize: 13,
                          color: videoMode === m.key ? '#1a237e' : '#666',
                        }}>
                        {m.icon}{m.label}
                      </Box>
                    ))}
                  </Box>
                </Grid>

                {videoMode === 'youtube' && (
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="YouTube URL *" value={form.YtLink} onChange={f('YtLink')}
                      placeholder="https://youtube.com/watch?v=..." />
                  </Grid>
                )}

                {videoMode === 'local' && (
                  <Grid item xs={12}>
                    <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }} disabled={videoUploading}>
                      {videoUploading ? <><CircularProgress size={14} sx={{ mr: 1 }} />Uploading video...</> :
                        videoFile ? `✓ ${videoFile.name}` : 'Upload Video File * (MP4, MOV, WEBM)'}
                      <input type="file" hidden accept="video/*" onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setVideoFile(file);
                        setVideoUploading(true);
                        const fd = new FormData();
                        fd.append('video-file', file);
                        const res = await postImg(Config.main_url + '/addVideoFile', fd);
                        setVideoUploading(false);
                        if (res?.code === '200') setVideoPath(res.videoName || '');
                        else { alert('Video upload failed: ' + (res?.message || 'Unknown')); setVideoFile(null); setVideoPath(''); }
                      }} />
                    </Button>
                    {videoFile && !videoUploading && (
                      <Box sx={{ mt: 1, p: 1.5, background: '#e8eaf6', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideoLibrary sx={{ color: '#1a237e', fontSize: 20 }} />
                        <Typography fontSize={12} color="#1a237e" fontWeight={600} noWrap>{videoFile.name}</Typography>
                      </Box>
                    )}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }} disabled={uploading}>
                    {uploading ? 'Uploading...' : image ? `✓ ${image.name}` : 'Upload Thumbnail (optional)'}
                    <input type="file" hidden accept="image/*" onChange={e => {
                      const file = e.target.files[0];
                      if (file) { setImage(file); handleImageUpload(file); }
                    }} />
                  </Button>
                  {image && !uploading && (
                    <Box component="img" src={URL.createObjectURL(image)} alt="preview"
                      sx={{ mt: 1, width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 2 }} />
                  )}
                </Grid>
              </>
            )}

            {config.hasText && (
              <>
                <Grid item xs={12}>
                  <TextField fullWidth size="small"
                    label={contentType === 'Testimonials' ? 'Name *' : 'Title *'}
                    value={contentType === 'Testimonials' ? form.name : form.tittle}
                    onChange={contentType === 'Testimonials' ? f('name') : f('tittle')} />
                </Grid>
                {(contentType === 'Blogs' || contentType === 'Programs') && (
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Description" value={form.description} onChange={f('description')} multiline rows={2} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Details" value={form.details} onChange={f('details')} multiline rows={2} />
                </Grid>
              </>
            )}

            {config.hasAgeGroup && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Age Group" value={form.age_group} onChange={f('age_group')} placeholder="e.g. 2-4 years" />
              </Grid>
            )}

            {config.hasRating && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Rating" value={form.rating} onChange={f('rating')}>
                  {[5, 4, 3, 2, 1].map(r => <MenuItem key={r} value={String(r)}>⭐ {r}</MenuItem>)}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSave} disabled={saving || uploading} fullWidth
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, py: 1.2 }}>
                {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {viewItem?.tittle || viewItem?.name || 'View'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {viewItem && getImageSrc(viewItem) && (
            <Box component="img" src={getImageSrc(viewItem)} alt="content"
              sx={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 2, mb: 2 }} />
          )}
          {viewItem?.YtLink && (
            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" href={viewItem.YtLink} target="_blank" startIcon={<YouTube />}
                sx={{ borderRadius: 2, color: '#FF0000', borderColor: '#FF0000' }}>
                Watch on YouTube
              </Button>
            </Box>
          )}
          {viewItem?.video_url && viewItem?.video_type === 'local' && (
            <Box sx={{ mb: 2 }}>
              <Box component="video" src={viewItem.video_url} controls
                sx={{ width: '100%', borderRadius: 2, maxHeight: 320, background: '#000' }} />
            </Box>
          )}
          {viewItem?.description && <Typography fontSize={13} color="text.secondary" mb={1}>{viewItem.description}</Typography>}
          {viewItem?.details && <Typography fontSize={13} color="text.secondary">{viewItem.details}</Typography>}
          {viewItem?.rating && <Chip label={`⭐ ${viewItem.rating}/5`} size="small" sx={{ mt: 1, background: '#fff3e0', color: '#e65100', fontWeight: 700 }} />}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setViewItem(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}
